// @flow
import { useRef, useEffect, useState, type Node } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import isFunction from 'lodash/isFunction';
import { animated, useSpring } from 'react-spring/web.cjs';
import { MOBILE_BREAKPOINT } from 'utils/theme';

type StyleType = { [string]: string | number };
type StyleGetterType = StyleType | (() => StyleType);
type PropsType = {
  children: Node,
  closedStyles?: StyleGetterType,
  isDesktopOnly?: boolean,
  isMobileOnly?: boolean,
  isOpen?: boolean,
  openStyles?: StyleGetterType
};

const Animator = animated(styled.div`
  overflow: hidden;

  ${({ isMobileOnly }) =>
    isMobileOnly
      ? css`
          display: none;
          @media ${MOBILE_BREAKPOINT} {
            display: block;
          }
        `
      : ''}
  ${({ isDesktopOnly }) =>
    isDesktopOnly
      ? css`
          display: block;
          @media ${MOBILE_BREAKPOINT} {
            display: none;
          }
        `
      : ''}
`);

function callIfFunction(objOrFunction: any): any {
  if (isFunction(objOrFunction)) return objOrFunction();
  return objOrFunction;
}

export default function ExpandAnimation({
  isOpen,
  openStyles = { height: 'auto' },
  closedStyles = { height: 0 },
  children,
  ...otherProps
}: PropsType) {
  const animatorRef = useRef();
  const [autoHeight, setAutoHeight] = useState();

  useEffect(() => {
    function onResize() {
      if (animatorRef.current) {
        setAutoHeight(animatorRef.current.getBoundingClientRect().height);
      }
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const springSettings = callIfFunction(isOpen ? openStyles : closedStyles);
  let { height } = springSettings;
  if (height === 'auto' && animatorRef.current) {
    height = autoHeight || 'auto';
  }

  const style = useSpring({
    from: {},
    to: {
      ...springSettings,
      height
    }
  });

  return (
    <Animator {...otherProps} style={style}>
      <div
        ref={(ref) => {
          animatorRef.current = ref;
          if (ref) setAutoHeight(ref.getBoundingClientRect().height);
        }}
      >
        {children}
      </div>
    </Animator>
  );
}
